import { formatDateTime } from "@/lib/format";
import { glopicApi } from "@/lib/glopic-api";
import Pagination from "@/components/base/Pagination";
import FilterBar from "@/components/base/FilterBar";
import TableSortHeader from "@/components/base/TableSortHeader";
import UserIdentityCell from "@/components/base/UserIdentityCell";
import { buildPointsFilters } from "./contracts";

interface PointsRow {
  id: number;
  user_id: string;
  user_type: "consumer" | "merchant";
  nickname: string | null;
  avatar: string | null;
  delta: number;
  balance_after: number;
  reason: string;
  ref_id: string | null;
  created_at: string;
}

const reasonLabels: Record<string, string> = {
  daily_checkin: "每日签到",
  vip_package_points: "VIP 礼包",
  generate_image: "生成图片",
  invite_reward: "邀请奖励",
  admin_adjust: "管理员调整",
};

export default function AiPointsLogPage() {
  const [points, setPoints] = useState<PointsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState("");
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const handleSort = useCallback((field: string, dir: "asc" | "desc") => {
    setSortField(field || "created_at");
    setSortDir(dir);
    setPage(1);
  }, []);

  const fetchPoints = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        ...buildPointsFilters(search, userType),
        page,
        page_size: pageSize,
      };

      const response = await glopicApi.get("/points-log", { params });
      const data = response.data.data;
      setPoints(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      console.error("Fetch points log error:", err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, userType, sortField, sortDir, page, pageSize]);

  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  const totalPages = Math.ceil(total / pageSize);
  const totalIncome = points.filter((p) => p.delta > 0).reduce((s, p) => s + p.delta, 0);
  const totalExpense = points.filter((p) => p.delta < 0).reduce((s, p) => s + p.delta, 0);

  const exportPoints = async () => {
    setExporting(true);
    try {
      const params = buildPointsFilters(search, userType);
      const response = await glopicApi.get("/points-log/export", { params, responseType: "blob" });
      downloadBlob(response.data, `glowpic-points-${userType || "all"}.csv`);
    } catch (err) {
      console.error("Export points log error:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">积分流水</h2>
          <p className="text-text-secondary text-sm mt-1">
            共 {total} 条记录 · 累计收入 +{totalIncome} · 累计支出 {totalExpense}
          </p>
        </div>
        <button
          onClick={() => void exportPoints()}
          disabled={exporting}
          className="h-9 px-3 rounded-lg border border-surface-border text-sm text-text-secondary hover:bg-surface disabled:opacity-50"
        >
          <i className="ri-download-line mr-1" />
          {exporting ? "导出中..." : "导出当前筛选"}
        </button>
      </div>

      {/* Filters */}
      <FilterBar
        search={search}
        onSearchChange={(val) => { setSearch(val); setPage(1); }}
        searchPlaceholder="搜索用户ID..."
        filters={[
          {
            key: "user_type",
            label: "全部用户类型",
            type: "select",
            options: [
              { value: "consumer", label: "C端用户" },
              { value: "merchant", label: "商家用户" },
            ],
          },
        ]}
        filterValues={{ user_type: userType }}
        onFilterChange={(key, value) => {
          if (key === "user_type") setUserType(value);
          setPage(1);
        }}
        onReset={() => { setSearch(""); setUserType(""); setSortField("created_at"); setSortDir("desc"); setPage(1); }}
      />

      {/* Table */}
      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="overflow-x-auto -mx-0">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">用户</th>
                <TableSortHeader label="变动" field="delta" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                <TableSortHeader label="余额" field="balance_after" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">来源</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">关联ID</th>
                <TableSortHeader label="时间" field="created_at" currentField={sortField} currentDir={sortDir} onSort={handleSort} className="hidden md:table-cell" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />
                    加载中...
                  </td>
                </tr>
              ) : points.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-coins-line text-3xl mb-2 block" />
                    暂无积分记录
                  </td>
                </tr>
              ) : (
                points.map((p) => (
                  <tr key={p.id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                    <td className="px-3 md:px-5 py-3 md:py-4">
                      <UserIdentityCell nickname={p.nickname} userId={p.user_id} avatar={p.avatar} />
                      <span className={`mt-1 inline-flex px-1.5 py-0.5 rounded text-[10px] ${p.user_type === "merchant" ? "bg-violet-500/10 text-violet-600" : "bg-blue-500/10 text-blue-600"}`}>
                        {p.user_type === "merchant" ? "商家用户" : "C端用户"}
                      </span>
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4">
                      <span className={`font-mono font-medium text-sm ${p.delta > 0 ? "text-emerald-500" : "text-red-500"}`}>
                        {p.delta > 0 ? "+" : ""}{p.delta}
                      </span>
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4 font-mono text-text-primary text-sm">{p.balance_after}</td>
                    <td className="px-3 md:px-5 py-3 md:py-4">
                      <span className="inline-flex px-2 py-1 rounded-full text-[11px] font-medium bg-surface text-text-secondary">
                        {reasonLabels[p.reason] || p.reason}
                      </span>
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4 text-text-muted text-xs font-mono">
                      {p.ref_id || "--"}
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4 text-text-muted text-xs hidden md:table-cell">
                      {formatDateTime(p.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </div>
    </div>
  );
}

function downloadBlob(data: string | Blob | ArrayBuffer, filename: string) {
  const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
